import * as React from "react";
import { cn } from "../../../lib/utils";
import {
  Radio,
  AlertTriangle,
  Server,
  Shield,
  Clock,
  Wifi,
  Lock,
  Download,
  FileCode,
  ChevronDown,
  ChevronUp,
  Terminal,
  Bug,
  TrendingUp,
  ExternalLink,
} from "lucide-react";
import { ModuleContainer, ModuleSection } from "../ModuleContainer";
import { IssuesList, type Issue } from "../IssuesList";
import { ModuleDataTable, type ColumnDef } from "../ModuleDataTable";
import { Badge } from "../../badge";
import { Button } from "../../button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../dropdown-menu";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";

// Types
type Protocol = "tcp" | "udp";
type PortState = "open" | "closed" | "filtered" | "open|filtered";
type DetectionMethod = "port_lookup" | "banner" | "probe" | "tls_cert" | "http_header";
type CveSeverity = "critical" | "high" | "medium" | "low" | "none";

interface ServiceInfo {
  name: string;
  product?: string;
  version?: string;
  banner?: string;
  extra_info?: string;
  confidence: number;
  method: DetectionMethod;
}

/** CVE vulnerability associated with a detected service */
interface PortVulnerability {
  cve_id: string;
  cvss_score?: number;
  severity: CveSeverity;
  description: string;
  /** EPSS score (0-1): probability of exploitation in next 30 days */
  epss_score?: number;
  /** EPSS percentile (0-1): how this CVE ranks compared to others */
  epss_percentile?: number;
  /** Whether this CVE is in CISA's Known Exploited Vulnerabilities catalog */
  is_kev?: boolean;
}

interface PortInfo {
  port: number;
  protocol: Protocol;
  state: PortState;
  service_hint?: string;
  service?: ServiceInfo;
  response_time_ms: number;
  /** CVEs affecting the detected service version */
  vulnerabilities?: PortVulnerability[];
  /** Whether this port has known vulnerabilities */
  is_vulnerable?: boolean;
  /** Highest CVE severity on this port */
  highest_severity?: CveSeverity;
  /** Count of critical/high severity CVEs */
  critical_cve_count?: number;
}

interface ScanStats {
  total_ports_scanned: number;
  tcp_open: number;
  tcp_closed: number;
  tcp_filtered: number;
  udp_open: number;
  udp_filtered: number;
  avg_response_time_ms: number;
}

/** Vulnerability summary across all scanned ports */
interface VulnSummary {
  total_cves: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
  vulnerable_ports: number;
  high_epss_count: number;
  kev_count: number;
}

export interface PortscanModuleData {
  score?: number;
  target?: string;
  ip?: string;
  source_subdomain?: string;
  tcp_ports?: PortInfo[];
  udp_ports?: PortInfo[];
  scan_duration_ms?: number;
  stats?: ScanStats;
  issues?: Array<string | Issue>;
  /** Vulnerability summary (present when CVE enrichment is enabled) */
  vuln_summary?: VulnSummary;
}

export interface PortscanModuleViewProps {
  data: PortscanModuleData;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  /** Hide the header (useful when rendered within tabs) */
  hideHeader?: boolean;
}

// Port state colors
const STATE_CONFIG: Record<PortState, { label: string; color: string }> = {
  open: { label: "Open", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
  closed: { label: "Closed", color: "bg-slate-500/15 text-slate-400 border-slate-500/40" },
  filtered: { label: "Filtered", color: "bg-amber-500/15 text-amber-400 border-amber-500/40" },
  "open|filtered": { label: "Open|Filtered", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/40" },
};

// Protocol colors
const PROTOCOL_CONFIG: Record<Protocol, { label: string; color: string }> = {
  tcp: { label: "TCP", color: "bg-blue-500/10 text-blue-400 border-blue-500/30" },
  udp: { label: "UDP", color: "bg-purple-500/10 text-purple-400 border-purple-500/30" },
};

// Common dangerous ports
const DANGEROUS_PORTS = new Set([21, 22, 23, 25, 135, 139, 445, 1433, 1521, 3306, 3389, 5432, 5900, 6379, 27017]);

// Detection method labels
const METHOD_LABELS: Record<DetectionMethod, string> = {
  port_lookup: "Port Lookup",
  banner: "Banner Grab",
  probe: "Active Probe",
  tls_cert: "TLS Certificate",
  http_header: "HTTP Headers",
};

// Pie chart colors for services
const SERVICE_COLORS = [
  "#22d3ee", // cyan-400
  "#818cf8", // indigo-400
  "#34d399", // emerald-400
  "#fbbf24", // amber-400
  "#f472b6", // pink-400
  "#a78bfa", // violet-400
  "#fb923c", // orange-400
  "#60a5fa", // blue-400
];

// CVE severity colors
const SEVERITY_CONFIG: Record<CveSeverity, { label: string; color: string; bgColor: string }> = {
  critical: { label: "Critical", color: "text-red-400", bgColor: "bg-red-500/15 border-red-500/40" },
  high: { label: "High", color: "text-orange-400", bgColor: "bg-orange-500/15 border-orange-500/40" },
  medium: { label: "Medium", color: "text-amber-400", bgColor: "bg-amber-500/15 border-amber-500/40" },
  low: { label: "Low", color: "text-blue-400", bgColor: "bg-blue-500/15 border-blue-500/40" },
  none: { label: "Info", color: "text-slate-400", bgColor: "bg-slate-500/15 border-slate-500/40" },
};

// Format EPSS score as percentage
function formatEpss(score?: number): string {
  if (score === undefined) return "—";
  return `${(score * 100).toFixed(1)}%`;
}

// CVE List Component for expandable display
function CveList({ vulnerabilities }: { vulnerabilities: PortVulnerability[] }) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const displayCount = isExpanded ? vulnerabilities.length : Math.min(3, vulnerabilities.length);
  const hasMore = vulnerabilities.length > 3;

  return (
    <div className="mt-2 space-y-2">
      <div className="flex items-center gap-2">
        <Bug className="h-3 w-3 text-red-400" />
        <span className="text-xs font-medium text-red-400">
          {vulnerabilities.length} CVE{vulnerabilities.length !== 1 ? "s" : ""} Found
        </span>
      </div>
      <div className="space-y-1.5">
        {vulnerabilities.slice(0, displayCount).map((vuln) => {
          const severityConfig = SEVERITY_CONFIG[vuln.severity];
          return (
            <div
              key={vuln.cve_id}
              className={cn(
                "p-2 rounded border text-xs",
                severityConfig.bgColor
              )}
            >
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <a
                    href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn("font-mono font-semibold hover:underline flex items-center gap-1", severityConfig.color)}
                  >
                    {vuln.cve_id}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                  <Badge variant="outline" className={cn("text-xs", severityConfig.bgColor, severityConfig.color)}>
                    {severityConfig.label}
                  </Badge>
                  {vuln.cvss_score !== undefined && (
                    <span className="text-muted-foreground">
                      CVSS: <span className={severityConfig.color}>{vuln.cvss_score.toFixed(1)}</span>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  {vuln.epss_score !== undefined && (
                    <span className="flex items-center gap-1" title="EPSS: Probability of exploitation in next 30 days">
                      <TrendingUp className="h-3 w-3" />
                      {formatEpss(vuln.epss_score)}
                    </span>
                  )}
                  {vuln.is_kev && (
                    <Badge variant="destructive" className="text-xs h-5">KEV</Badge>
                  )}
                </div>
              </div>
              <p className="mt-1 text-muted-foreground line-clamp-2">{vuln.description}</p>
            </div>
          );
        })}
      </div>
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="h-6 text-xs w-full"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              Show {vulnerabilities.length - 3} more CVE{vulnerabilities.length - 3 !== 1 ? "s" : ""}
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// Vulnerability Summary Card
function VulnSummaryCard({ summary }: { summary: VulnSummary }) {
  if (summary.total_cves === 0) return null;

  return (
    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
      <div className="flex items-center gap-3 mb-3">
        <Bug className="h-5 w-5 text-red-400" />
        <div>
          <h4 className="font-medium text-red-400">Vulnerability Summary</h4>
          <p className="text-sm text-muted-foreground">
            {summary.total_cves} CVE{summary.total_cves !== 1 ? "s" : ""} found across {summary.vulnerable_ports} port{summary.vulnerable_ports !== 1 ? "s" : ""}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {summary.critical > 0 && (
          <div className="p-2 rounded bg-red-500/20 border border-red-500/30 text-center">
            <p className="text-xs text-muted-foreground">Critical</p>
            <p className="text-xl font-bold text-red-400">{summary.critical}</p>
          </div>
        )}
        {summary.high > 0 && (
          <div className="p-2 rounded bg-orange-500/20 border border-orange-500/30 text-center">
            <p className="text-xs text-muted-foreground">High</p>
            <p className="text-xl font-bold text-orange-400">{summary.high}</p>
          </div>
        )}
        {summary.medium > 0 && (
          <div className="p-2 rounded bg-amber-500/20 border border-amber-500/30 text-center">
            <p className="text-xs text-muted-foreground">Medium</p>
            <p className="text-xl font-bold text-amber-400">{summary.medium}</p>
          </div>
        )}
        {summary.low > 0 && (
          <div className="p-2 rounded bg-blue-500/20 border border-blue-500/30 text-center">
            <p className="text-xs text-muted-foreground">Low</p>
            <p className="text-xl font-bold text-blue-400">{summary.low}</p>
          </div>
        )}
      </div>
      {(summary.high_epss_count > 0 || summary.kev_count > 0) && (
        <div className="mt-3 pt-3 border-t border-red-500/30 flex items-center gap-4 text-sm">
          {summary.high_epss_count > 0 && (
            <div className="flex items-center gap-1 text-orange-400">
              <TrendingUp className="h-4 w-4" />
              <span>{summary.high_epss_count} with high exploitation risk (EPSS {">"} 10%)</span>
            </div>
          )}
          {summary.kev_count > 0 && (
            <div className="flex items-center gap-1 text-red-400">
              <AlertTriangle className="h-4 w-4" />
              <span>{summary.kev_count} in CISA KEV catalog</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Export ports as CSV (now includes CVE data)
function exportPortsCsv(ports: PortInfo[], target?: string) {
  const headers = ["Port", "Protocol", "State", "Service", "Product", "Version", "Banner", "Response Time (ms)", "CVE Count", "Highest Severity", "CVE IDs"];
  const csvContent = [
    headers.join(","),
    ...ports.map((p) => [
      p.port,
      p.protocol.toUpperCase(),
      p.state,
      `"${p.service?.name || p.service_hint || ""}"`,
      `"${p.service?.product || ""}"`,
      `"${p.service?.version || ""}"`,
      `"${(p.service?.banner || "").replace(/"/g, '""')}"`,
      p.response_time_ms,
      p.vulnerabilities?.length || 0,
      p.highest_severity || "",
      `"${(p.vulnerabilities || []).map(v => v.cve_id).join("; ")}"`,
    ].join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `portscan_${target || "scan"}_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Export as Nmap XML format
function exportNmapXml(data: PortscanModuleData) {
  const escapeXml = (str: string) => str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

  const tcpPorts = data.tcp_ports || [];
  const udpPorts = data.udp_ports || [];
  const allPorts = [...tcpPorts, ...udpPorts];
  const openPorts = allPorts.filter(p => p.state === "open" || p.state === "open|filtered");

  const scanTime = new Date().toISOString();
  const startTime = Math.floor(Date.now() / 1000);

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE nmaprun>
<nmaprun scanner="asm-toolkit" args="portscan" start="${startTime}" startstr="${scanTime}" version="1.0" xmloutputversion="1.05">
<scaninfo type="syn" protocol="tcp" numservices="${tcpPorts.length}" services="${tcpPorts.map(p => p.port).join(',')}"/>
<host starttime="${startTime}" endtime="${startTime + Math.floor((data.scan_duration_ms || 0) / 1000)}">
<status state="up" reason="user-set"/>
<address addr="${escapeXml(data.ip || data.target || 'unknown')}" addrtype="ipv4"/>
${data.target && data.target !== data.ip ? `<hostnames><hostname name="${escapeXml(data.target)}" type="user"/></hostnames>` : '<hostnames/>'}
<ports>
${openPorts.map(p => `<port protocol="${p.protocol}" portid="${p.port}">
<state state="${p.state}" reason="syn-ack" reason_ttl="64"/>
${p.service ? `<service name="${escapeXml(p.service.name)}"${p.service.product ? ` product="${escapeXml(p.service.product)}"` : ''}${p.service.version ? ` version="${escapeXml(p.service.version)}"` : ''} method="${p.service.method}" conf="${p.service.confidence}"/>` : `<service name="${escapeXml(p.service_hint || 'unknown')}" method="table" conf="1"/>`}
</port>`).join('\n')}
</ports>
<times srtt="${Math.round((data.stats?.avg_response_time_ms || 0) * 1000)}" rttvar="0" to="100000"/>
</host>
<runstats><finished time="${startTime + Math.floor((data.scan_duration_ms || 0) / 1000)}" timestr="${scanTime}" elapsed="${((data.scan_duration_ms || 0) / 1000).toFixed(2)}" summary="Nmap done at ${scanTime}; 1 IP address (1 host up) scanned in ${((data.scan_duration_ms || 0) / 1000).toFixed(2)} seconds" exit="success"/><hosts up="1" down="0" total="1"/></runstats>
</nmaprun>`;

  const blob = new Blob([xml], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `portscan_${data.target || data.ip || "scan"}_${new Date().toISOString().split("T")[0]}.xml`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Service Pie Chart Component
function ServicePieChart({ ports }: { ports: PortInfo[] }) {
  const serviceData = React.useMemo(() => {
    const counts: Record<string, number> = {};
    ports.forEach((p) => {
      const service = p.service?.name || p.service_hint || "Unknown";
      counts[service] = (counts[service] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8); // Top 8 services
  }, [ports]);

  if (serviceData.length === 0) return null;

  return (
    <div className="h-[200px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={serviceData}
            cx="50%"
            cy="50%"
            innerRadius={40}
            outerRadius={70}
            paddingAngle={2}
            dataKey="value"
            nameKey="name"
          >
            {serviceData.map((_, index) => (
              <Cell key={`cell-${index}`} fill={SERVICE_COLORS[index % SERVICE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--popover))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "0.5rem",
            }}
            itemStyle={{ color: "hsl(var(--foreground))" }}
          />
          <Legend
            wrapperStyle={{ fontSize: "12px" }}
            formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

// Banner Display Component
function BannerDisplay({ banner }: { banner?: string }) {
  const [isExpanded, setIsExpanded] = React.useState(false);

  if (!banner) return null;

  const isLong = banner.length > 100;
  const displayBanner = isLong && !isExpanded ? banner.slice(0, 100) + "..." : banner;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-1">
        <Terminal className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Banner</span>
      </div>
      <pre className="text-xs font-mono bg-muted/50 p-2 rounded border border-border overflow-x-auto whitespace-pre-wrap break-all">
        {displayBanner}
      </pre>
      {isLong && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-1 h-6 text-xs"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3 mr-1" />
              Show more
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// Format duration
function formatDuration(ms?: number): string {
  if (!ms) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

// Normalize issues
function normalizeIssues(issues?: Array<string | Issue>): Issue[] {
  if (!issues) return [];
  return issues.map((issue, index) => {
    if (typeof issue === "string") {
      let severity: Issue["severity"] = "info";
      if (issue.toLowerCase().includes("telnet") || issue.toLowerCase().includes("ftp") || issue.toLowerCase().includes("rdp")) {
        severity = "high";
      } else if (issue.toLowerCase().includes("open")) {
        severity = "medium";
      }
      return { id: `portscan-issue-${index}`, severity, message: issue, module: "portscan" };
    }
    return issue;
  });
}

export function PortscanModuleView({
  data,
  isLoading,
  error,
  onRetry,
  className,
  hideHeader,
}: PortscanModuleViewProps) {
  const issues = normalizeIssues(data.issues);
  const tcpPorts = data.tcp_ports || [];
  const udpPorts = data.udp_ports || [];
  const stats = data.stats;

  // All open ports
  const openTcp = tcpPorts.filter(p => p.state === "open");
  const openUdp = udpPorts.filter(p => p.state === "open" || p.state === "open|filtered");

  // Dangerous open ports
  const dangerousOpen = [...openTcp, ...openUdp].filter(p => DANGEROUS_PORTS.has(p.port));

  // Port columns
  const columns: ColumnDef<PortInfo>[] = [
    {
      id: "port",
      header: "Port",
      accessor: (row) => row.port,
      cell: (value, row) => (
        <div className="flex items-center gap-2">
          <code className={cn(
            "text-sm font-mono font-semibold",
            DANGEROUS_PORTS.has(row.port) && row.state === "open" ? "text-red-400" : ""
          )}>
            {value as number}
          </code>
          {DANGEROUS_PORTS.has(row.port) && row.state === "open" && (
            <AlertTriangle className="h-3 w-3 text-red-400" />
          )}
        </div>
      ),
      sortable: true,
      width: "100px",
    },
    {
      id: "protocol",
      header: "Protocol",
      accessor: (row) => row.protocol,
      cell: (value) => {
        const config = PROTOCOL_CONFIG[value as Protocol];
        return <Badge variant="outline" className={cn("text-xs", config.color)}>{config.label}</Badge>;
      },
      sortable: true,
      width: "80px",
    },
    {
      id: "state",
      header: "State",
      accessor: (row) => row.state,
      cell: (value) => {
        const config = STATE_CONFIG[value as PortState];
        return <Badge variant="outline" className={cn("text-xs", config.color)}>{config.label}</Badge>;
      },
      sortable: true,
      filterable: true,
      width: "120px",
    },
    {
      id: "service",
      header: "Service",
      accessor: (row) => row.service?.name || row.service_hint || "",
      cell: (_, row) => {
        const service = row.service;
        const hint = row.service_hint;

        if (service) {
          return (
            <div>
              <div className="flex items-center flex-wrap gap-1">
                <span className="text-sm font-medium">{service.name}</span>
                {service.product && (
                  <span className="text-xs text-muted-foreground">
                    ({service.product}{service.version && ` ${service.version}`})
                  </span>
                )}
                {service.method && service.method !== "port_lookup" && (
                  <Badge variant="outline" className="text-xs">{METHOD_LABELS[service.method]}</Badge>
                )}
              </div>
              {/* Banner display */}
              <BannerDisplay banner={service.banner} />
              {/* CVE list (expandable) */}
              {row.vulnerabilities && row.vulnerabilities.length > 0 && (
                <CveList vulnerabilities={row.vulnerabilities} />
              )}
            </div>
          );
        }

        return hint ? (
          <span className="text-sm text-muted-foreground">{hint}</span>
        ) : (
          <span className="text-xs text-muted-foreground">Unknown</span>
        );
      },
      sortable: true,
      filterable: true,
    },
    {
      id: "vulnerabilities",
      header: "CVEs",
      accessor: (row) => row.vulnerabilities?.length || 0,
      cell: (value, row) => {
        const count = value as number;
        if (count === 0) {
          return <span className="text-xs text-muted-foreground">—</span>;
        }
        const severityConfig = row.highest_severity ? SEVERITY_CONFIG[row.highest_severity] : SEVERITY_CONFIG.none;
        return (
          <div className="flex items-center gap-1">
            <Badge variant="outline" className={cn("text-xs", severityConfig.bgColor, severityConfig.color)}>
              {count} CVE{count !== 1 ? "s" : ""}
            </Badge>
            {(row.critical_cve_count || 0) > 0 && (
              <span className="text-xs text-red-400 font-medium">
                ({row.critical_cve_count} critical/high)
              </span>
            )}
          </div>
        );
      },
      sortable: true,
      width: "140px",
    },
    {
      id: "response_time_ms",
      header: "Response",
      accessor: (row) => row.response_time_ms,
      cell: (value) => (
        <span className="text-xs font-mono text-muted-foreground">{value as number}ms</span>
      ),
      sortable: true,
      align: "right",
      width: "100px",
    },
  ];

  // Combine ports for table
  const allPorts = [...tcpPorts, ...udpPorts].filter(p => p.state === "open" || p.state === "open|filtered");

  return (
    <ModuleContainer
      moduleName="portscan"
      icon={Radio}
      data={data}
      isLoading={isLoading}
      error={error}
      score={data.score}
      onRetry={onRetry}
      className={className}
      hideHeader={hideHeader}
    >
      <div className="space-y-6">
        {/* Summary with Export */}
        <div className="flex items-start justify-between gap-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Wifi className="h-3 w-3 text-emerald-400" /> TCP Open
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-emerald-400">
                  {stats?.tcp_open || openTcp.length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Radio className="h-3 w-3 text-purple-400" /> UDP Open
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold font-mono text-purple-400">
                  {stats?.udp_open || openUdp.length}
                </p>
              </CardContent>
            </Card>
            <Card className={cn("bg-card/50", dangerousOpen.length > 0 && "border-red-500/30")}>
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Shield className="h-3 w-3 text-red-400" /> Risky Ports
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className={cn("text-2xl font-bold font-mono", dangerousOpen.length > 0 ? "text-red-400" : "text-emerald-400")}>
                  {dangerousOpen.length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground font-normal flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Scan Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg font-semibold">{formatDuration(data.scan_duration_ms)}</p>
              </CardContent>
            </Card>
          </div>
          {/* Export Dropdown */}
          {allPorts.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1.5">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => exportPortsCsv(allPorts, data.target)}>
                  <Download className="h-4 w-4 mr-2" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => exportNmapXml(data)}>
                  <FileCode className="h-4 w-4 mr-2" />
                  Export as Nmap XML
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Target Info */}
        {(data.target || data.ip) && (
          <div className="p-4 rounded-lg bg-muted/30 border border-border">
            <div className="flex items-center gap-4">
              <Server className="h-5 w-5 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">Target</p>
                <div className="flex items-center gap-2">
                  <code className="text-sm font-mono">{data.target || data.ip}</code>
                  {data.ip && data.target && data.ip !== data.target && (
                    <Badge variant="outline" className="text-xs">{data.ip}</Badge>
                  )}
                </div>
              </div>
              {data.source_subdomain && (
                <div className="border-l border-border pl-4">
                  <p className="text-xs text-muted-foreground">Source Subdomain</p>
                  <code className="text-sm font-mono">{data.source_subdomain}</code>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dangerous Ports Alert */}
        {dangerousOpen.length > 0 && (
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div>
                <h4 className="font-medium text-red-400">Potentially Risky Services Exposed</h4>
                <p className="text-sm text-muted-foreground">
                  {dangerousOpen.length} port(s) with potentially sensitive services are open: {" "}
                  {dangerousOpen.map(p => `${p.port}/${p.protocol}`).join(", ")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Vulnerability Summary (if CVE enrichment is available) */}
        {data.vuln_summary && data.vuln_summary.total_cves > 0 && (
          <VulnSummaryCard summary={data.vuln_summary} />
        )}

        {/* Scan Statistics */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="p-3 rounded-lg bg-muted/30 border border-border text-center">
              <p className="text-xs text-muted-foreground">Ports Scanned</p>
              <p className="text-lg font-bold font-mono">{stats.total_ports_scanned}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30 border border-border text-center">
              <p className="text-xs text-muted-foreground">TCP Closed</p>
              <p className="text-lg font-bold font-mono">{stats.tcp_closed}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30 border border-border text-center">
              <p className="text-xs text-muted-foreground">TCP Filtered</p>
              <p className="text-lg font-bold font-mono">{stats.tcp_filtered}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30 border border-border text-center">
              <p className="text-xs text-muted-foreground">UDP Filtered</p>
              <p className="text-lg font-bold font-mono">{stats.udp_filtered}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/30 border border-border text-center">
              <p className="text-xs text-muted-foreground">Avg Response</p>
              <p className="text-lg font-bold font-mono">{stats.avg_response_time_ms.toFixed(0)}ms</p>
            </div>
          </div>
        )}

        {/* Service Distribution Chart */}
        {allPorts.length > 1 && (
          <ModuleSection
            title="Service Distribution"
            icon={Server}
            badge={<Badge variant="outline" className="text-xs">{allPorts.length} services</Badge>}
          >
            <ServicePieChart ports={allPorts} />
          </ModuleSection>
        )}

        {/* Open Ports Table */}
        {allPorts.length > 0 && (
          <ModuleSection
            title="Open Ports"
            icon={Lock}
            badge={<Badge variant="outline" className="text-xs">{allPorts.length}</Badge>}
          >
            <ModuleDataTable
              data={allPorts}
              columns={columns}
              paginated={allPorts.length > 15}
              searchable
              searchPlaceholder="Search ports or services..."
            />
          </ModuleSection>
        )}

        {/* Issues */}
        {issues.length > 0 && (
          <ModuleSection
            title="Security Issues"
            badge={<Badge variant="outline" className="text-xs bg-amber-500/10 border-amber-500/30 text-amber-400">{issues.length}</Badge>}
          >
            <IssuesList issues={issues} groupBySeverity showCounts />
          </ModuleSection>
        )}
      </div>
    </ModuleContainer>
  );
}

PortscanModuleView.displayName = "PortscanModuleView";
